import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { Gear, XCircle } from '@phosphor-icons/react';
import { memo, useEffect, useState } from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { useAuthStore } from '../../../store/storeAuth';
import { useDebounceEffect } from '../../../util/useDebounceEffect';
import FormNode from '../Components/FormNode/FormNode';

const FLOW_OPTIONS = {
  ALL_FLOWS: 'all-flows',
  SPECIFIC_FLOW: '',
};

const FILE_OPTIONS = {
  RETAIN: 'retain',
  REMOVE: 'remove',
};

const SubmitButtonNode = ({ id, type, data }: NodeProps) => {
  const [formData, setFormData] = useState<{
    btnName: string;
    flows: any[];
    removeOrRetain: string;
    submitType: string;
  }>({
    btnName: '',
    flows: data?.flows || [],
    removeOrRetain: data?.removeOrRetain || FILE_OPTIONS.RETAIN,
    submitType: data?.submitType || FLOW_OPTIONS.ALL_FLOWS,
  });
  const [changes, setChanges] = useState<any>({
    directly: false,
    data: {},
  });
  const reactFlow = useReactFlow();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { user } = useAuthStore(state => state);

  const [optionsSelectedFlows, setOptionsSelectedFlows] = useState<any[]>([]);
  const [selectedFlows, setSelectedFlows] = useState('');
  const [radioOption, setRadioOption] = useState(FLOW_OPTIONS.ALL_FLOWS);
  const [removeOrRetain, setRemoveOrRetain] = useState(FILE_OPTIONS.RETAIN);
  const [flowToSubmit, setFlowToSubmit] = useState<any[]>([]);

  const initializeFromData = () => {
    if (!data) return;
    
    if (data.flowsButtons) {
      const flowNames = data.flowsButtons.map((flow: any) => flow.name);
      setOptionsSelectedFlows(flowNames);
    }

    if (data.flows?.length > 0 && data.submitType !== FLOW_OPTIONS.ALL_FLOWS) {
      const flowId = data.flows[0];
      const matchingFlow = data.flowsButtons?.find((flow: any) => flow.id === flowId);
      
      if (matchingFlow) {
        setSelectedFlows(matchingFlow.name);
        setFlowToSubmit([flowId]);
        setRadioOption(FLOW_OPTIONS.SPECIFIC_FLOW);
      }
    } else {
      setRadioOption(FLOW_OPTIONS.ALL_FLOWS);
      if (data.flowsButtons) {
        setFlowToSubmit(data.flowsButtons.map((flow: any) => flow.id));
      }
      setSelectedFlows('');
    }

    setRemoveOrRetain(data.removeOrRetain || FILE_OPTIONS.RETAIN);
  };

  useEffect(() => {
    initializeFromData();
  }, [data]);

  useDebounceEffect(
    () => {
      if (changes.directly) {
        const nodes = reactFlow.getNodes();

        const mappedNodes = nodes.map(node => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                ...changes.data,
              },
            };
          }
          return node;
        });

        reactFlow.setNodes(mappedNodes);

        setChanges({ directly: false });
      }
    },
    [changes],
    500,
  );

  const handleClickPopover = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  function handleChangeFlows(value: string) {
    if (value === FLOW_OPTIONS.ALL_FLOWS) {
      handleSelectAllFlows();
    } else {
      handleSelectSpecificFlow(value);
    }
  }
  
  function handleSelectAllFlows() {
    if (data?.flowsButtons) {
      setFlowToSubmit(data.flowsButtons.map((flow: any) => flow.id));
    } else {
      setFlowToSubmit([]);
    }
    setSelectedFlows('');
    setRadioOption(FLOW_OPTIONS.ALL_FLOWS);
  }
  
  function handleSelectSpecificFlow(flowName: string) {
    const selectedFlow = data?.flowsButtons?.find(
      (flow: any) => flow.name === flowName
    );
    
    if (selectedFlow) {
      setFlowToSubmit([selectedFlow.id]);
      setSelectedFlows(flowName);
      setRadioOption(FLOW_OPTIONS.SPECIFIC_FLOW);
    }
  }

  function handleSubmit() {
    const updatedData = {
      flows: flowToSubmit,
      removeOrRetain,
      submitType: radioOption === FLOW_OPTIONS.ALL_FLOWS ? FLOW_OPTIONS.ALL_FLOWS : FLOW_OPTIONS.SPECIFIC_FLOW,
    };

    setFormData(prevData => ({
      ...prevData,
      ...updatedData,
    }));

    setChanges({
      directly: true,
      data: updatedData,
    });
    
    setAnchorEl(null);
  }

  return (
    <>
      <FormNode id={id} type={type}>
        <Box display="flex" justifyContent={'space-between'}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClickPopover}
            sx={{
              width: '200px',
              fontSize: '12px',
              gap: '8px',
            }}
          >
            Advanced Settings
            <Gear size={16} />
          </Button>

          <Button variant="contained">Submit</Button>
        </Box>
      </FormNode>

      <Popover
        id={id}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          width={'362px'}
          height={'100%'}
          p={'16px'}
          borderRadius="16px"
          gap={'16px'}
        >
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography fontSize={'14ox'} fontWeight={600} color={'#000000'}>
              Submission Options or Flow Selection
            </Typography>
            <XCircle
              size={24}
              color="#667085"
              cursor={'pointer'}
              onClick={() => setAnchorEl(null)}
            />
          </Box>

          <FormControl sx={{ mb: '0px' }}>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              name="radio-buttons-group"
              value={radioOption}
              onChange={e => handleChangeFlows(e.target.value)}
            >
              <FormControlLabel
                value="all-flows"
                control={<Radio />}
                label="Submit to all flows"
              />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ mt: '-10px' }}>Or</Divider>

          <FormControl>
            <InputLabel id="select-label-submit" sx={{ mt: '-7px' }}>
              Submit to
            </InputLabel>
            <Select
              labelId="select-label-submit"
              label="Submit to"
              className="nodrag"
              fullWidth
              size="small"
              value={selectedFlows}
              onChange={e => handleChangeFlows(e.target.value)}
              sx={{
                height: 'unset',
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              }}
            >
              {optionsSelectedFlows.map((flow: any) => (
                <MenuItem value={flow}>{flow}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
            <Typography fontSize={'14ox'} fontWeight={600} color={'#000000'}>
              Submission Options or Flow Selection
            </Typography>

            <FormControl>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                value={removeOrRetain}
                onChange={e => setRemoveOrRetain(e.target.value)}
              >
                <FormControlLabel
                  value="remove"
                  control={<Radio />}
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#475467',
                  }}
                  label="Remove uploaded files (Submitted file uploads will be removed following each flow run)"
                />

                <FormControlLabel
                  value="retain"
                  control={<Radio />}
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#475467',
                    mt: '16px',
                  }}
                  label="Retain uploaded files (Submitted file uploads will be saved in the system after flow is run)"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box display={'flex'} justifyContent={'flex-end'}>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default memo(SubmitButtonNode);